package com.havn.app.core.designsystem

import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.path
import androidx.compose.ui.unit.dp

enum class HavnIconKey {
    Back,
    TodayGrid,
    Today,
    Properties,
    ContactCard,
    CalendarClock,
    Leads,
    Reports,
    Settings,
    Sliders,
    Calendar,
    Building,
    Home,
    User,
    Users,
    Mail,
    Phone,
    Search,
    Refresh,
    Plus,
    LogOut,
    Chart,
    TrendUp,
    Check,
    Alert,
    Trash,
    Star,
    Message,
    Note,
    More,
    Send,
    QrCode,
    ArrowRight,
    Google,
    Apple
}

@Composable
fun HavnIcon(
    icon: HavnIconKey,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    tint: Color? = null
) {
    Icon(
        imageVector = HavnIcons.vectorFor(icon),
        contentDescription = contentDescription,
        modifier = modifier,
        tint = tint ?: LocalContentColor.current
    )
}

object HavnIcons {
    fun vectorFor(icon: HavnIconKey): ImageVector = when (icon) {
        HavnIconKey.Back -> Back
        HavnIconKey.TodayGrid -> TodayGrid
        HavnIconKey.Today -> Today
        HavnIconKey.Properties -> Properties
        HavnIconKey.ContactCard -> ContactCard
        HavnIconKey.CalendarClock -> CalendarClock
        HavnIconKey.Leads -> Leads
        HavnIconKey.Reports -> Reports
        HavnIconKey.Settings -> Settings
        HavnIconKey.Sliders -> Sliders
        HavnIconKey.Calendar -> Calendar
        HavnIconKey.Building -> Building
        HavnIconKey.Home -> Home
        HavnIconKey.User -> User
        HavnIconKey.Users -> Users
        HavnIconKey.Mail -> Mail
        HavnIconKey.Phone -> Phone
        HavnIconKey.Search -> Search
        HavnIconKey.Refresh -> Refresh
        HavnIconKey.Plus -> Plus
        HavnIconKey.LogOut -> LogOut
        HavnIconKey.Chart -> Chart
        HavnIconKey.TrendUp -> TrendUp
        HavnIconKey.Check -> Check
        HavnIconKey.Alert -> Alert
        HavnIconKey.Trash -> Trash
        HavnIconKey.Star -> Star
        HavnIconKey.Message -> Message
        HavnIconKey.Note -> Note
        HavnIconKey.More -> More
        HavnIconKey.Send -> Send
        HavnIconKey.QrCode -> QrCode
        HavnIconKey.ArrowRight -> ArrowRight
        HavnIconKey.Google -> Google
        HavnIconKey.Apple -> Apple
    }

    val Back: ImageVector by lazyIcon("Back") {
        line { moveTo(19f, 12f); horizontalLineTo(5f) }
        line { moveTo(11f, 6f); lineTo(5f, 12f); lineTo(11f, 18f) }
    }

    val ArrowRight: ImageVector by lazyIcon("ArrowRight") {
        line { moveTo(5f, 12f); horizontalLineTo(19f) }
        line { moveTo(13f, 6f); lineTo(19f, 12f); lineTo(13f, 18f) }
    }

    val Refresh: ImageVector by lazyIcon("Refresh") {
        line {
            moveTo(20f, 11f)
            curveTo(19.6f, 7.6f, 16.7f, 5f, 13.2f, 5f)
            curveTo(10.8f, 5f, 8.6f, 6.2f, 7.4f, 8.1f)
        }
        line { moveTo(7f, 4.8f); verticalLineTo(8.4f); horizontalLineTo(10.6f) }
        line {
            moveTo(4f, 13f)
            curveTo(4.4f, 16.4f, 7.3f, 19f, 10.8f, 19f)
            curveTo(13.2f, 19f, 15.4f, 17.8f, 16.6f, 15.9f)
        }
        line { moveTo(17f, 19.2f); verticalLineTo(15.6f); horizontalLineTo(13.4f) }
    }

    val Star: ImageVector by lazyIcon("Star") {
        line {
            moveTo(12f, 3.5f)
            lineTo(14.7f, 8.8f)
            lineTo(20.5f, 9.6f)
            lineTo(16.3f, 13.7f)
            lineTo(17.3f, 19.5f)
            lineTo(12f, 16.8f)
            lineTo(6.7f, 19.5f)
            lineTo(7.7f, 13.7f)
            lineTo(3.5f, 9.6f)
            lineTo(9.3f, 8.8f)
            close()
        }
    }

    val Message: ImageVector by lazyIcon("Message") {
        line {
            moveTo(5f, 5.5f)
            horizontalLineTo(19f)
            curveTo(20.1f, 5.5f, 21f, 6.4f, 21f, 7.5f)
            verticalLineTo(15.5f)
            curveTo(21f, 16.6f, 20.1f, 17.5f, 19f, 17.5f)
            horizontalLineTo(11f)
            lineTo(6f, 20.5f)
            verticalLineTo(17.5f)
            horizontalLineTo(5f)
            curveTo(3.9f, 17.5f, 3f, 16.6f, 3f, 15.5f)
            verticalLineTo(7.5f)
            curveTo(3f, 6.4f, 3.9f, 5.5f, 5f, 5.5f)
        }
        line { moveTo(7.5f, 10f); horizontalLineTo(16.5f) }
        line { moveTo(7.5f, 13.5f); horizontalLineTo(13.5f) }
    }

    val Note: ImageVector by lazyIcon("Note") {
        line {
            moveTo(6f, 4f)
            horizontalLineTo(15f)
            lineTo(19f, 8f)
            verticalLineTo(20f)
            horizontalLineTo(6f)
            curveTo(5.4f, 20f, 5f, 19.6f, 5f, 19f)
            verticalLineTo(5f)
            curveTo(5f, 4.4f, 5.4f, 4f, 6f, 4f)
        }
        line { moveTo(14.5f, 4.5f); verticalLineTo(8.5f); horizontalLineTo(18.5f) }
        line { moveTo(8f, 13f); horizontalLineTo(16f) }
        line { moveTo(8f, 16.5f); horizontalLineTo(13f) }
    }

    val More: ImageVector by lazyIcon("More") {
        line { moveTo(6f, 12f); verticalLineTo(12.1f) }
        line { moveTo(12f, 12f); verticalLineTo(12.1f) }
        line { moveTo(18f, 12f); verticalLineTo(12.1f) }
    }

    val Send: ImageVector by lazyIcon("Send") {
        line {
            moveTo(21f, 4f)
            lineTo(10f, 15f)
            lineTo(8.5f, 21f)
            lineTo(5.8f, 12.2f)
            lineTo(3f, 10.5f)
            close()
        }
        line { moveTo(10f, 15f); lineTo(15f, 20f) }
    }

    val QrCode: ImageVector by lazyIcon("QrCode") {
        line { moveTo(4f, 4f); horizontalLineTo(10f); verticalLineTo(10f); horizontalLineTo(4f); close() }
        line { moveTo(14f, 4f); horizontalLineTo(20f); verticalLineTo(10f); horizontalLineTo(14f); close() }
        line { moveTo(4f, 14f); horizontalLineTo(10f); verticalLineTo(20f); horizontalLineTo(4f); close() }
        line { moveTo(15f, 15f); horizontalLineTo(17.5f); verticalLineTo(17.5f) }
        line { moveTo(20f, 14f); verticalLineTo(20f); horizontalLineTo(14f) }
        line { moveTo(6.5f, 6.5f); horizontalLineTo(7.5f); verticalLineTo(7.5f); horizontalLineTo(6.5f); close() }
        line { moveTo(16.5f, 6.5f); horizontalLineTo(17.5f); verticalLineTo(7.5f); horizontalLineTo(16.5f); close() }
        line { moveTo(6.5f, 16.5f); horizontalLineTo(7.5f); verticalLineTo(17.5f); horizontalLineTo(6.5f); close() }
    }

    val Building: ImageVector by lazyIcon("Building") {
        line {
            moveTo(4.5f, 20f)
            verticalLineTo(7f)
            curveTo(4.5f, 6.4f, 4.9f, 6f, 5.5f, 6f)
            horizontalLineTo(11f)
            curveTo(11.6f, 6f, 12f, 6.4f, 12f, 7f)
            verticalLineTo(20f)
        }
        line {
            moveTo(12f, 10f)
            horizontalLineTo(18.5f)
            curveTo(19.1f, 10f, 19.5f, 10.4f, 19.5f, 11f)
            verticalLineTo(20f)
        }
        line { moveTo(3f, 20f); horizontalLineTo(21f) }
        line { moveTo(7f, 9f); horizontalLineTo(9.5f) }
        line { moveTo(7f, 12.5f); horizontalLineTo(9.5f) }
        line { moveTo(7f, 16f); horizontalLineTo(9.5f) }
        line { moveTo(14.5f, 13f); horizontalLineTo(17f) }
        line { moveTo(14.5f, 16.5f); horizontalLineTo(17f) }
    }

    val Google: ImageVector by lazyIcon("Google") {
        line {
            moveTo(20.5f, 12.2f)
            curveTo(20.5f, 11.5f, 20.4f, 10.9f, 20.3f, 10.3f)
            horizontalLineTo(12.2f)
        }
        line {
            moveTo(20.1f, 12.2f)
            curveTo(20.1f, 17f, 16.9f, 20.5f, 12.1f, 20.5f)
            curveTo(7.4f, 20.5f, 3.6f, 16.7f, 3.6f, 12f)
            curveTo(3.6f, 7.3f, 7.4f, 3.5f, 12.1f, 3.5f)
            curveTo(14.4f, 3.5f, 16.3f, 4.3f, 17.8f, 5.7f)
        }
        line {
            moveTo(12.2f, 16.1f)
            curveTo(14.2f, 16.1f, 15.6f, 15.1f, 16.2f, 13.3f)
        }
    }

    val Apple: ImageVector by lazyIcon("Apple") {
        line {
            moveTo(15.3f, 3.2f)
            curveTo(15.2f, 4.2f, 14.8f, 5.1f, 14.1f, 5.8f)
            curveTo(13.4f, 6.5f, 12.6f, 6.9f, 11.7f, 6.8f)
            curveTo(11.8f, 5.8f, 12.2f, 5f, 12.9f, 4.3f)
            curveTo(13.6f, 3.6f, 14.5f, 3.2f, 15.3f, 3.2f)
        }
        line {
            moveTo(18.4f, 17.1f)
            curveTo(17.9f, 18.3f, 17.2f, 19.5f, 16.4f, 20.4f)
            curveTo(15.7f, 21.2f, 14.9f, 21.3f, 14f, 20.9f)
            curveTo(13.2f, 20.6f, 12.5f, 20.6f, 11.7f, 20.9f)
            curveTo(10.7f, 21.3f, 9.9f, 21.2f, 9.2f, 20.4f)
            curveTo(7.5f, 18.6f, 6.2f, 15.2f, 6.9f, 12.4f)
            curveTo(7.4f, 10.3f, 8.9f, 8.9f, 10.7f, 8.9f)
            curveTo(11.6f, 8.9f, 12.1f, 9.3f, 12.8f, 9.3f)
            curveTo(13.5f, 9.3f, 14.2f, 8.8f, 15.3f, 8.9f)
            curveTo(16.5f, 9f, 17.5f, 9.5f, 18.2f, 10.4f)
            curveTo(16.4f, 11.5f, 16.3f, 14.1f, 18.4f, 15.1f)
        }
    }

    val Today: ImageVector by lazyIcon("Today") {
        line { moveTo(8f, 2.5f); verticalLineTo(5.5f) }
        line { moveTo(16f, 2.5f); verticalLineTo(5.5f) }
        line { moveTo(4f, 9f); horizontalLineTo(20f) }
        line {
            moveTo(5.5f, 4f)
            horizontalLineTo(18.5f)
            curveTo(19.6f, 4f, 20.5f, 4.9f, 20.5f, 6f)
            verticalLineTo(19f)
            curveTo(20.5f, 20.1f, 19.6f, 21f, 18.5f, 21f)
            horizontalLineTo(5.5f)
            curveTo(4.4f, 21f, 3.5f, 20.1f, 3.5f, 19f)
            verticalLineTo(6f)
            curveTo(3.5f, 4.9f, 4.4f, 4f, 5.5f, 4f)
        }
        line { moveTo(8f, 13f); horizontalLineTo(16f) }
        line { moveTo(8f, 17f); horizontalLineTo(12f) }
    }

    val TodayGrid: ImageVector by lazyIcon("TodayGrid") {
        line {
            moveTo(5.5f, 5.5f)
            horizontalLineTo(18.5f)
            curveTo(19.6f, 5.5f, 20.5f, 6.4f, 20.5f, 7.5f)
            verticalLineTo(18.5f)
            curveTo(20.5f, 19.6f, 19.6f, 20.5f, 18.5f, 20.5f)
            horizontalLineTo(5.5f)
            curveTo(4.4f, 20.5f, 3.5f, 19.6f, 3.5f, 18.5f)
            verticalLineTo(7.5f)
            curveTo(3.5f, 6.4f, 4.4f, 5.5f, 5.5f, 5.5f)
        }
        line { moveTo(8f, 3.5f); verticalLineTo(7.5f) }
        line { moveTo(16f, 3.5f); verticalLineTo(7.5f) }
        line { moveTo(3.5f, 10f); horizontalLineTo(20.5f) }
        line { moveTo(8.2f, 13.4f); verticalLineTo(13.5f) }
        line { moveTo(12f, 13.4f); verticalLineTo(13.5f) }
        line { moveTo(15.8f, 13.4f); verticalLineTo(13.5f) }
        line { moveTo(8.2f, 17f); verticalLineTo(17.1f) }
        line { moveTo(12f, 17f); verticalLineTo(17.1f) }
        line { moveTo(15.8f, 17f); verticalLineTo(17.1f) }
    }

    val Properties: ImageVector by lazyIcon("Properties") {
        line {
            moveTo(3.5f, 11f)
            lineTo(12f, 4f)
            lineTo(20.5f, 11f)
        }
        line {
            moveTo(5.5f, 10f)
            verticalLineTo(20f)
            horizontalLineTo(18.5f)
            verticalLineTo(10f)
        }
        line {
            moveTo(9.5f, 20f)
            verticalLineTo(14f)
            horizontalLineTo(14.5f)
            verticalLineTo(20f)
        }
    }

    val Trash: ImageVector by lazyIcon("Trash") {
        line { moveTo(4f, 7f); horizontalLineTo(20f) }
        line { moveTo(10f, 11f); verticalLineTo(17f) }
        line { moveTo(14f, 11f); verticalLineTo(17f) }
        line {
            moveTo(6.5f, 7f)
            lineTo(7.4f, 20f)
            curveTo(7.5f, 20.8f, 8.2f, 21.5f, 9f, 21.5f)
            horizontalLineTo(15f)
            curveTo(15.8f, 21.5f, 16.5f, 20.8f, 16.6f, 20f)
            lineTo(17.5f, 7f)
        }
        line {
            moveTo(9f, 7f)
            verticalLineTo(4.5f)
            curveTo(9f, 3.7f, 9.7f, 3f, 10.5f, 3f)
            horizontalLineTo(13.5f)
            curveTo(14.3f, 3f, 15f, 3.7f, 15f, 4.5f)
            verticalLineTo(7f)
        }
    }

    val Leads: ImageVector by lazyIcon("Leads") {
        line {
            moveTo(14.9f, 19.5f)
            curveTo(14.9f, 17.2f, 13.1f, 15.4f, 10.8f, 15.4f)
            curveTo(8.5f, 15.4f, 6.7f, 17.2f, 6.7f, 19.5f)
        }
        line {
            moveTo(10.8f, 12.4f)
            curveTo(9.2f, 12.4f, 7.9f, 11.1f, 7.9f, 9.5f)
            curveTo(7.9f, 7.9f, 9.2f, 6.6f, 10.8f, 6.6f)
            curveTo(12.4f, 6.6f, 13.7f, 7.9f, 13.7f, 9.5f)
            curveTo(13.7f, 11.1f, 12.4f, 12.4f, 10.8f, 12.4f)
        }
        line {
            moveTo(21f, 18.8f)
            curveTo(21f, 16.9f, 19.6f, 15.5f, 17.8f, 15.2f)
        }
        line {
            moveTo(16f, 6.9f)
            curveTo(17.5f, 7.1f, 18.6f, 8.3f, 18.6f, 9.8f)
            curveTo(18.6f, 11.1f, 17.8f, 12.2f, 16.6f, 12.6f)
        }
    }

    val ContactCard: ImageVector by lazyIcon("ContactCard") {
        line {
            moveTo(7.4f, 3.9f)
            horizontalLineTo(17.9f)
            curveTo(19f, 3.9f, 19.9f, 4.8f, 19.9f, 5.9f)
        }
        line {
            moveTo(5.7f, 5.8f)
            horizontalLineTo(18.2f)
            curveTo(19.3f, 5.8f, 20.2f, 6.7f, 20.2f, 7.8f)
            verticalLineTo(17f)
        }
        line {
            moveTo(6f, 5f)
            horizontalLineTo(16.7f)
            curveTo(17.8f, 5f, 18.7f, 5.9f, 18.7f, 7f)
            verticalLineTo(18.1f)
            curveTo(18.7f, 19.2f, 17.8f, 20.1f, 16.7f, 20.1f)
            horizontalLineTo(5.3f)
            curveTo(4.2f, 20.1f, 3.3f, 19.2f, 3.3f, 18.1f)
            verticalLineTo(7f)
            curveTo(3.3f, 5.9f, 4.2f, 5f, 5.3f, 5f)
        }
        line {
            moveTo(9.2f, 12.7f)
            curveTo(8f, 12.7f, 7.1f, 11.8f, 7.1f, 10.6f)
            curveTo(7.1f, 9.4f, 8f, 8.5f, 9.2f, 8.5f)
            curveTo(10.4f, 8.5f, 11.3f, 9.4f, 11.3f, 10.6f)
            curveTo(11.3f, 11.8f, 10.4f, 12.7f, 9.2f, 12.7f)
        }
        line {
            moveTo(6.2f, 16.7f)
            curveTo(6.6f, 15.4f, 7.8f, 14.5f, 9.2f, 14.5f)
            curveTo(10.6f, 14.5f, 11.8f, 15.4f, 12.2f, 16.7f)
        }
        line { moveTo(13.8f, 10f); horizontalLineTo(16.1f) }
        line { moveTo(13.8f, 13f); horizontalLineTo(16.1f) }
        line { moveTo(13.8f, 16f); horizontalLineTo(15.7f) }
    }

    val CalendarClock: ImageVector by lazyIcon("CalendarClock") {
        line { moveTo(8f, 3.5f); verticalLineTo(6.5f) }
        line { moveTo(16f, 3.5f); verticalLineTo(6.5f) }
        line { moveTo(4f, 9f); horizontalLineTo(20f) }
        line {
            moveTo(5.5f, 5f)
            horizontalLineTo(18.5f)
            curveTo(19.6f, 5f, 20.5f, 5.9f, 20.5f, 7f)
            verticalLineTo(16.2f)
            curveTo(20.5f, 17.3f, 19.6f, 18.2f, 18.5f, 18.2f)
            horizontalLineTo(5.5f)
            curveTo(4.4f, 18.2f, 3.5f, 17.3f, 3.5f, 16.2f)
            verticalLineTo(7f)
            curveTo(3.5f, 5.9f, 4.4f, 5f, 5.5f, 5f)
        }
        line {
            moveTo(16.6f, 21f)
            curveTo(18.9f, 21f, 20.8f, 19.1f, 20.8f, 16.8f)
            curveTo(20.8f, 14.5f, 18.9f, 12.6f, 16.6f, 12.6f)
            curveTo(14.3f, 12.6f, 12.4f, 14.5f, 12.4f, 16.8f)
            curveTo(12.4f, 19.1f, 14.3f, 21f, 16.6f, 21f)
        }
        line { moveTo(16.6f, 14.8f); verticalLineTo(17f); lineTo(18.1f, 17.8f) }
    }

    val Reports: ImageVector by lazyIcon("Reports") {
        line {
            moveTo(7f, 3.5f)
            horizontalLineTo(15f)
            lineTo(19f, 7.5f)
            verticalLineTo(20.5f)
            horizontalLineTo(7f)
            curveTo(5.9f, 20.5f, 5f, 19.6f, 5f, 18.5f)
            verticalLineTo(5.5f)
            curveTo(5f, 4.4f, 5.9f, 3.5f, 7f, 3.5f)
        }
        line { moveTo(14.8f, 3.8f); verticalLineTo(8f); horizontalLineTo(19f) }
        line { moveTo(8.5f, 13f); horizontalLineTo(15.5f) }
        line { moveTo(8.5f, 16.5f); horizontalLineTo(13f) }
    }

    val Settings: ImageVector by lazyIcon("Settings") {
        line {
            moveTo(12f, 15.5f)
            curveTo(10.1f, 15.5f, 8.5f, 13.9f, 8.5f, 12f)
            curveTo(8.5f, 10.1f, 10.1f, 8.5f, 12f, 8.5f)
            curveTo(13.9f, 8.5f, 15.5f, 10.1f, 15.5f, 12f)
            curveTo(15.5f, 13.9f, 13.9f, 15.5f, 12f, 15.5f)
        }
        line {
            moveTo(19.4f, 13.2f)
            lineTo(20.7f, 14.2f)
            lineTo(18.9f, 17.3f)
            lineTo(17.3f, 16.7f)
            curveTo(16.8f, 17.1f, 16.3f, 17.4f, 15.7f, 17.6f)
            lineTo(15.4f, 19.3f)
            horizontalLineTo(11.8f)
            lineTo(11.5f, 17.6f)
            curveTo(10.9f, 17.4f, 10.3f, 17.1f, 9.9f, 16.7f)
            lineTo(8.3f, 17.3f)
            lineTo(6.5f, 14.2f)
            lineTo(7.8f, 13.2f)
            curveTo(7.7f, 12.8f, 7.6f, 12.4f, 7.6f, 12f)
            curveTo(7.6f, 11.6f, 7.7f, 11.2f, 7.8f, 10.8f)
            lineTo(6.5f, 9.8f)
            lineTo(8.3f, 6.7f)
            lineTo(9.9f, 7.3f)
            curveTo(10.3f, 6.9f, 10.9f, 6.6f, 11.5f, 6.4f)
            lineTo(11.8f, 4.7f)
            horizontalLineTo(15.4f)
            lineTo(15.7f, 6.4f)
            curveTo(16.3f, 6.6f, 16.8f, 6.9f, 17.3f, 7.3f)
            lineTo(18.9f, 6.7f)
            lineTo(20.7f, 9.8f)
            lineTo(19.4f, 10.8f)
        }
    }

    val Sliders: ImageVector by lazyIcon("Sliders") {
        line { moveTo(4f, 7f); horizontalLineTo(10f) }
        line { moveTo(14f, 7f); horizontalLineTo(20f) }
        line {
            moveTo(12f, 5f)
            curveTo(13.1f, 5f, 14f, 5.9f, 14f, 7f)
            curveTo(14f, 8.1f, 13.1f, 9f, 12f, 9f)
            curveTo(10.9f, 9f, 10f, 8.1f, 10f, 7f)
            curveTo(10f, 5.9f, 10.9f, 5f, 12f, 5f)
        }
        line { moveTo(4f, 17f); horizontalLineTo(11f) }
        line { moveTo(15f, 17f); horizontalLineTo(20f) }
        line {
            moveTo(13f, 15f)
            curveTo(14.1f, 15f, 15f, 15.9f, 15f, 17f)
            curveTo(15f, 18.1f, 14.1f, 19f, 13f, 19f)
            curveTo(11.9f, 19f, 11f, 18.1f, 11f, 17f)
            curveTo(11f, 15.9f, 11.9f, 15f, 13f, 15f)
        }
    }

    val Calendar: ImageVector get() = Today
    val Home: ImageVector get() = Properties

    val User: ImageVector by lazyIcon("User") {
        line {
            moveTo(16f, 20f)
            curveTo(16f, 17.8f, 14.2f, 16f, 12f, 16f)
            curveTo(9.8f, 16f, 8f, 17.8f, 8f, 20f)
        }
        line {
            moveTo(12f, 12f)
            curveTo(10.1f, 12f, 8.5f, 10.4f, 8.5f, 8.5f)
            curveTo(8.5f, 6.6f, 10.1f, 5f, 12f, 5f)
            curveTo(13.9f, 5f, 15.5f, 6.6f, 15.5f, 8.5f)
            curveTo(15.5f, 10.4f, 13.9f, 12f, 12f, 12f)
        }
    }

    val Users: ImageVector by lazyIcon("Users") {
        line {
            moveTo(16f, 20f)
            curveTo(16f, 17.8f, 14.2f, 16f, 12f, 16f)
            curveTo(9.8f, 16f, 8f, 17.8f, 8f, 20f)
        }
        line {
            moveTo(12f, 12f)
            curveTo(10.1f, 12f, 8.5f, 10.4f, 8.5f, 8.5f)
            curveTo(8.5f, 6.6f, 10.1f, 5f, 12f, 5f)
            curveTo(13.9f, 5f, 15.5f, 6.6f, 15.5f, 8.5f)
            curveTo(15.5f, 10.4f, 13.9f, 12f, 12f, 12f)
        }
        line {
            moveTo(4.5f, 18.5f)
            curveTo(4.5f, 16.8f, 5.8f, 15.5f, 7.5f, 15.5f)
        }
        line {
            moveTo(7.3f, 12.2f)
            curveTo(5.9f, 12f, 4.8f, 10.8f, 4.8f, 9.4f)
            curveTo(4.8f, 8.1f, 5.7f, 7f, 7f, 6.7f)
        }
        line {
            moveTo(19.5f, 18.5f)
            curveTo(19.5f, 16.8f, 18.2f, 15.5f, 16.5f, 15.5f)
        }
        line {
            moveTo(16.7f, 12.2f)
            curveTo(18.1f, 12f, 19.2f, 10.8f, 19.2f, 9.4f)
            curveTo(19.2f, 8.1f, 18.3f, 7f, 17f, 6.7f)
        }
    }

    val Mail: ImageVector by lazyIcon("Mail") {
        line {
            moveTo(4.5f, 6f)
            horizontalLineTo(19.5f)
            curveTo(20.3f, 6f, 21f, 6.7f, 21f, 7.5f)
            verticalLineTo(17f)
            curveTo(21f, 17.8f, 20.3f, 18.5f, 19.5f, 18.5f)
            horizontalLineTo(4.5f)
            curveTo(3.7f, 18.5f, 3f, 17.8f, 3f, 17f)
            verticalLineTo(7.5f)
            curveTo(3f, 6.7f, 3.7f, 6f, 4.5f, 6f)
        }
        line { moveTo(4f, 7f); lineTo(12f, 13f); lineTo(20f, 7f) }
    }

    val Phone: ImageVector by lazyIcon("Phone") {
        line {
            moveTo(8f, 4.5f)
            lineTo(10f, 8.5f)
            lineTo(8.4f, 10.1f)
            curveTo(9.6f, 12.6f, 11.4f, 14.4f, 13.9f, 15.6f)
            lineTo(15.5f, 14f)
            lineTo(19.5f, 16f)
            lineTo(18.5f, 20f)
            curveTo(10.7f, 19.8f, 4.2f, 13.3f, 4f, 5.5f)
            lineTo(8f, 4.5f)
        }
    }

    val Search: ImageVector by lazyIcon("Search") {
        line {
            moveTo(11f, 17f)
            curveTo(7.7f, 17f, 5f, 14.3f, 5f, 11f)
            curveTo(5f, 7.7f, 7.7f, 5f, 11f, 5f)
            curveTo(14.3f, 5f, 17f, 7.7f, 17f, 11f)
            curveTo(17f, 14.3f, 14.3f, 17f, 11f, 17f)
        }
        line { moveTo(15.5f, 15.5f); lineTo(20f, 20f) }
    }

    val Plus: ImageVector by lazyIcon("Plus") {
        line { moveTo(12f, 5f); verticalLineTo(19f) }
        line { moveTo(5f, 12f); horizontalLineTo(19f) }
    }

    val LogOut: ImageVector by lazyIcon("LogOut") {
        line { moveTo(14f, 8f); verticalLineTo(6f); curveTo(14f, 4.9f, 13.1f, 4f, 12f, 4f); horizontalLineTo(6f); curveTo(4.9f, 4f, 4f, 4.9f, 4f, 6f); verticalLineTo(18f); curveTo(4f, 19.1f, 4.9f, 20f, 6f, 20f); horizontalLineTo(12f); curveTo(13.1f, 20f, 14f, 19.1f, 14f, 18f); verticalLineTo(16f) }
        line { moveTo(10f, 12f); horizontalLineTo(20f) }
        line { moveTo(17f, 9f); lineTo(20f, 12f); lineTo(17f, 15f) }
    }

    val Chart: ImageVector by lazyIcon("Chart") {
        line { moveTo(5f, 19f); verticalLineTo(11f) }
        line { moveTo(12f, 19f); verticalLineTo(5f) }
        line { moveTo(19f, 19f); verticalLineTo(8f) }
    }

    val TrendUp: ImageVector by lazyIcon("TrendUp") {
        line { moveTo(4f, 16f); lineTo(9f, 11f); lineTo(13f, 15f); lineTo(20f, 8f) }
        line { moveTo(15f, 8f); horizontalLineTo(20f); verticalLineTo(13f) }
    }

    val Check: ImageVector by lazyIcon("Check") {
        line { moveTo(5f, 12.5f); lineTo(10f, 17.5f); lineTo(19f, 6.5f) }
    }

    val Alert: ImageVector by lazyIcon("Alert") {
        line { moveTo(12f, 7f); verticalLineTo(13f) }
        line { moveTo(12f, 17f); verticalLineTo(17.1f) }
        line {
            moveTo(11f, 4.5f)
            curveTo(11.4f, 3.8f, 12.6f, 3.8f, 13f, 4.5f)
            lineTo(21f, 18.5f)
            curveTo(21.4f, 19.2f, 20.9f, 20f, 20f, 20f)
            horizontalLineTo(4f)
            curveTo(3.1f, 20f, 2.6f, 19.2f, 3f, 18.5f)
            lineTo(11f, 4.5f)
        }
    }
}

private fun lazyIcon(
    name: String,
    block: androidx.compose.ui.graphics.vector.PathBuilder.() -> Unit
): Lazy<ImageVector> = lazy {
    ImageVector.Builder(
        name = "Havn$name",
        defaultWidth = 24.dp,
        defaultHeight = 24.dp,
        viewportWidth = 24f,
        viewportHeight = 24f
    ).apply {
        path(
            fill = null,
            stroke = SolidColor(Color.Black),
            strokeLineWidth = 1.8f,
            strokeLineCap = StrokeCap.Round,
            strokeLineJoin = StrokeJoin.Round,
            pathBuilder = block
        )
    }.build()
}

private fun androidx.compose.ui.graphics.vector.PathBuilder.line(
    block: androidx.compose.ui.graphics.vector.PathBuilder.() -> Unit
) {
    block()
}
